import React, { useMemo } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../components/primitives/SMLButton';
import { Panel } from '../../components/primitives/Panel';
import { sourceLook } from '../streamingSources';
import { streamingPeriodMessages as messages } from '../streamingPeriodMessages';
import { SourceMark } from '../parts/SourceMark';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import shared from '../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SectionEmptyStateProps {
  title: MessageDescriptor;
  text: MessageDescriptor;
  /** The kinds of source that give this data (`sections[key].givenBy`). */
  givenBy: readonly string[];
  testId: string;
  onOpenCatalog: () => void;
}

/**
 * No connected source gives the data of the section (design streaming-sections e2): one setup card
 * with one link to Catalog, and the cards of the sources that give it. The cards are information
 * only.
 */
const SectionEmptyStateComponent: React.FC<SectionEmptyStateProps> = ({
  title,
  text,
  givenBy,
  testId,
  onOpenCatalog,
}) => {
  const intl = useIntl();
  const cards = useMemo(
    () =>
      givenBy.map((integrationType) => (
        <div key={integrationType} className={shared.infoCard}>
          <div className={shared.infoCardHead}>
            <SourceMark integrationType={integrationType} />
            <span>{sourceLook(integrationType).name}</span>
          </div>
        </div>
      )),
    [givenBy]
  );

  return (
    <div className={shared.page} data-testid={testId}>
      <Panel nested className={shared.card}>
        <h3>{intl.formatMessage(title)}</h3>
        <p>{withBrandWord(intl.formatMessage(text))}</p>
        <SMLButton variant="primary" onClick={onOpenCatalog}>
          {intl.formatMessage(messages.connectInCatalog)}
        </SMLButton>
      </Panel>
      <h3 className={shared.blockTitle}>{intl.formatMessage(messages.givenByTitle)}</h3>
      {cards.length > 0 ? (
        <div className={shared.infoCards}>{cards}</div>
      ) : (
        <p className={shared.faint}>{withBrandWord(intl.formatMessage(messages.givenByNone))}</p>
      )}
    </div>
  );
};

export const SectionEmptyState = TypedMemo(SectionEmptyStateComponent);
